/**
 * VIP plans the fan sees in /sub.
 *
 *  - First day (the calendar day of the fan's first visit): only the
 *    "Primer día" offer at the first-day price.
 *  - From then on: the month at the regular price, and multi-month promos
 *    priced as months × monthly price minus the tier's discount.
 *
 * Tiers: config.master.json → sales.promos in the real build (the backend
 * prices them the same way in backend/src/lib/plans.js); PLAN_TIERS below in the demo.
 */
import { useEffect, useState } from 'react'
import type { AppConfig } from '@/hooks/useAppConfig'
import { IS_DEMO } from './demo'
import { useDemoCustomization } from './customizer'
import { CREATOR_PLANS } from './creator'

const DEFAULT_TIERS: { months: number; discount: number }[] = [
  { months: 1, discount: 0 },
  { months: 2, discount: 0.1 },
  { months: 6, discount: 0.1 },
  { months: 8, discount: 0.1 },
]

export const PLAN_TIERS = CREATOR_PLANS?.length ? CREATOR_PLANS : DEFAULT_TIERS

export interface Plan {
  /** first_day | m1 | m2 | m6 | m8 */
  id: string
  months: number
  /** Total price for the whole period (COP) */
  price: number
  /** Price without the discount, to show crossed out */
  fullPrice: number
  discount: number
  stars: number
  /** Fixed USD price for the whole period (VITE_USD_MONTHLY × months − discount); 0 = not shown */
  usd: number
  firstDay: boolean
  /** Legacy plan name the payment API knows: monthly = new-fan price, yearly = recurrent price */
  apiPlan: 'monthly' | 'yearly'
}

const roundTo = (n: number, step: number) => Math.round(n / step) * step

export function buildPlans(config: AppConfig, firstDay: boolean): Plan[] {
  const first = config.pricing.vipMonthly || 30000
  const monthly = config.pricing.vipYearly || 50000
  const starsFirst = config.pricing.stars_price_new || 450
  const starsMonthly = config.pricing.stars_price_recurrent || 750
  const usdMonthly = IS_DEMO ? 0 : Number(import.meta.env.VITE_USD_MONTHLY) || 0
  if (firstDay) {
    return [{ id: 'first_day', months: 1, price: first, fullPrice: first, discount: 0, stars: starsFirst, usd: usdMonthly, firstDay: true, apiPlan: 'monthly' }]
  }
  return PLAN_TIERS.map(({ months, discount }) => {
    const full = monthly * months
    return {
      id: `m${months}`,
      months,
      price: discount ? roundTo(full * (1 - discount), 100) : full,
      fullPrice: full,
      discount,
      stars: Math.round(starsMonthly * months * (1 - discount)),
      usd: Math.round(usdMonthly * months * (1 - discount)),
      firstDay: false,
      apiPlan: 'yearly' as const,
    }
  })
}

// ─── First day ──────────────────────────────────────────────────────────────
const FIRST_SEEN_KEY = 'ct_first_seen'
const dayKey = (ts: number) => new Date(ts).toLocaleDateString('en-CA')

function readLocalFirstSeen(): number {
  try {
    const v = Number(localStorage.getItem(FIRST_SEEN_KEY))
    if (Number.isFinite(v) && v > 0) return v
    const now = Date.now()
    localStorage.setItem(FIRST_SEEN_KEY, String(now))
    return now
  } catch {
    return Date.now()
  }
}

/**
 * True on the calendar day of the fan's first visit. The first visit is kept
 * in localStorage and, inside Telegram, in CloudStorage (follows the fan
 * across devices; the earliest of both wins).
 */
// Una sola consulta por sesión: ¿el servidor considera recurrente a este fan?
let serverRecurrent: Promise<boolean | null> | null = null
function fetchServerRecurrent(): Promise<boolean | null> {
  if (serverRecurrent) return serverRecurrent
  const initData = window.Telegram?.WebApp?.initData
  if (IS_DEMO || !initData) return (serverRecurrent = Promise.resolve(null))
  const api = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/$/, '')
  serverRecurrent = fetch(`${api}/api/plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData }),
  })
    .then((r) => r.json())
    .then((d) => (d?.ok ? Boolean(d.isRecurrent) : null))
    .catch(() => null)
  return serverRecurrent
}

export function useIsFirstDay(): boolean {
  const view = useDemoCustomization().content?.pricingView
  const [firstSeen, setFirstSeen] = useState(readLocalFirstSeen)
  // El precio de primer día lo decide el servidor (un fan que ya fue VIP nunca lo recibe):
  // si dice que es recurrente, no se muestra la oferta ni su cuenta regresiva aunque sea su "primer día" local
  const [recurrent, setRecurrent] = useState<boolean | null>(null)
  useEffect(() => {
    let alive = true
    fetchServerRecurrent().then((v) => { if (alive) setRecurrent(v) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    const cs = window.Telegram?.WebApp?.CloudStorage
    if (!cs || !window.Telegram?.WebApp?.isVersionAtLeast?.('6.9')) return
    try {
      cs.getItem(FIRST_SEEN_KEY, (err: unknown, value?: string) => {
        const cloud = Number(value)
        if (!err && Number.isFinite(cloud) && cloud > 0 && cloud < firstSeen) {
          setFirstSeen(cloud)
          try { localStorage.setItem(FIRST_SEEN_KEY, String(cloud)) } catch { /* ignore */ }
        } else if (!err && !(cloud > 0)) {
          cs.setItem(FIRST_SEEN_KEY, String(firstSeen), () => {})
        }
      })
    } catch { /* old clients */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Demo: the customizer can preview either state
  if (IS_DEMO && view) return view === 'first'
  if (recurrent) return false
  return dayKey(firstSeen) === dayKey(Date.now())
}

export function planLabel(p: Plan, lang: string): string {
  const es = lang !== 'en'
  if (p.firstDay) return es ? 'Primer día' : 'First day'
  if (p.months === 1) return es ? '1 mes' : '1 month'
  return es ? `${p.months} meses` : `${p.months} months`
}

export function planDesc(p: Plan, lang: string): string {
  const es = lang !== 'en'
  if (p.firstDay) return es ? 'Precio especial solo hoy, en tu primer día. 30 días de acceso.' : 'Special price only today, your first day. 30 days of access.'
  if (!p.discount) return es ? '30 días de acceso al canal VIP.' : '30 days of VIP channel access.'
  const days = p.months * 30
  const pct = Math.round(p.discount * 100)
  return es ? `${days} días de acceso. Ahorras ${pct}% frente a pagar mes a mes.` : `${days} days of access. Save ${pct}% versus paying monthly.`
}
